import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { SubPage } from "@/components/sub-page";
import { supabase } from "@/integrations/supabase/client";
import { Pager } from "@/components/pager";

export const Route = createFileRoute("/comunicaciones/podcast/")({
  head: () => ({
    meta: [
      { title: "Podcast Historias que Escalan | Mine Class" },
      { name: "description", content: "Historias que Escalan, el podcast de Mine Class: entrevistas, relatos y conversaciones con protagonistas de la industria minera." },
      { property: "og:title", content: "Podcast Historias que Escalan | Mine Class" },
      { property: "og:description", content: "Escucha entrevistas y relatos de líderes, trabajadores y expertos de la minería en el podcast de Mine Class." },
      { property: "og:url", content: "/comunicaciones/podcast" },
    ],
    links: [{ rel: "canonical", href: "/comunicaciones/podcast" }],
  }),
  component: Page,
});

type Episode = {
  id: string;
  title: string;
  subtitle: string | null;
  episode_number: number | null;
  description: string | null;
  spotify_embed_html: string | null;
  youtube_embed_html: string | null;
  cover_image_url: string | null;
  published_at: string | null;
};

function extractYouTubeId(html: string | null): string | null {
  if (!html) return null;
  const m = html.match(/(?:youtube\.com\/(?:embed\/|watch\?v=)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
}

function Page() {
  const [items, setItems] = useState<Episode[]>([]);
  const [page, setPage] = useState(1);
  const perPage = 5;
  const totalPages = Math.max(1, Math.ceil(items.length / perPage));
  const current = items.slice((page - 1) * perPage, page * perPage);
  const goto = (p: number) => {
    setPage(p);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("podcast_episodes" as never)
        .select("id,title,subtitle,episode_number,description,spotify_embed_html,youtube_embed_html,cover_image_url,published_at")
        .eq("is_published", true)
        .order("episode_number", { ascending: false, nullsFirst: false });
      setItems((data ?? []) as Episode[]);
    })();
  }, []);

  return (
    <SubPage
      section="comunicaciones"
      eyebrow="Comunicaciones"
      title="Historias que Escalan"
      intro="Nuestro podcast con las historias detrás de la industria minera."
    >
      {items.length === 0 ? (
        <div className="text-center py-16 border border-[var(--color-outline-variant)] bg-white not-prose">
          <p className="text-[var(--color-on-surface-variant)]">Próximamente nuevos episodios.</p>
        </div>
      ) : (
        <>
          <div className="space-y-6 not-prose">
            {current.map((ep) => {
              const ytId = extractYouTubeId(ep.youtube_embed_html);
              const rectImg = ep.cover_image_url ?? (ytId ? `https://i.ytimg.com/vi/${ytId}/maxresdefault.jpg` : null);
              return (
                <Link
                  key={ep.id}
                  to="/comunicaciones/podcast/$id"
                  params={{ id: ep.id }}
                  className="block bg-white border border-[var(--color-outline-variant)] hover:border-[var(--color-brand-coral)] transition-colors overflow-hidden"
                >
                  <div className="grid md:grid-cols-[1fr_minmax(0,45%)] gap-0">
                    <div className="p-6 md:p-8">
                      {ep.episode_number != null && (
                        <span className="text-xs uppercase tracking-widest text-[var(--color-brand-coral)] font-bold">
                          Episodio #{ep.episode_number}
                        </span>
                      )}
                      <h3 className="text-2xl font-bold text-[var(--color-brand-purple)] mt-1">{ep.title}</h3>
                      {ep.subtitle && <p className="text-[var(--color-on-surface-variant)] mt-1">{ep.subtitle}</p>}
                      {ep.description && <p className="text-sm mt-3 line-clamp-4">{ep.description}</p>}
                      <span className="inline-block mt-4 text-sm font-bold text-[var(--color-brand-coral)]">
                        Escuchar episodio →
                      </span>
                    </div>
                    {rectImg ? (
                      <div className="relative w-full h-48 md:h-full min-h-[220px] bg-black">
                        <img src={rectImg} alt={ep.title} className="absolute inset-0 w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-full h-48 md:h-full min-h-[220px] bg-[var(--color-surface-container-low)]" />
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
          <Pager page={page} totalPages={totalPages} onChange={goto} />
        </>
      )}
    </SubPage>
  );
}
